// useAdaptiveTabFit — measures the contract tab bar's geometry so the pure
// splitTabFit computation can decide the visible/overflow split.
//
// The caller (ContractTabBar) owns the refs and renders an aria-hidden
// measurement row of all tabs plus a More button at full size, inside a visible
// container. It passes those refs in; the hook reads the per-item and
// More-button widths in a useLayoutEffect and tracks the container width with a
// ResizeObserver. (Passing refs in rather than returning ref callbacks matches
// the codebase's useFocusTrap pattern and keeps ref ownership with the caller.)
//
// Re-measurement is keyed on itemCount + remeasureKey. The caller serialises
// the badge counts into remeasureKey because a badge growing (9 -> 100) widens
// its tab, which changes the fit. In jsdom (and any environment where
// getBoundingClientRect returns 0), `measured` is false so the caller can fall
// back to showing every tab with no More button.

import { useLayoutEffect, useState, type RefObject } from "react";

// Used when the row's computed column-gap is unparseable — jsdom returns
// ""/"normal" for a flex gap it never laid out. Matches the live row's gap-8
// (2rem at the 16px root font size).
const FALLBACK_GAP_PX = 32;

export interface UseAdaptiveTabFitArgs {
  /** Container whose inner width bounds the visible row. */
  containerRef: RefObject<HTMLElement | null>;
  /** The live primary row carrying the flex gap between tabs; its computed
   *  column-gap feeds the fit math so the CSS gap stays the single source of
   *  truth. */
  rowRef: RefObject<HTMLElement | null>;
  /** One entry per tab (in editorial order) from the hidden measurement row.
   *  Null holes are tolerated (treated as zero width). */
  itemRefs: RefObject<Array<HTMLElement | null>>;
  /** The hidden measurement row's More button. */
  moreRef: RefObject<HTMLElement | null>;
  itemCount: number;
  /** Serialised badge counts (or any width-affecting state) — a change forces
   *  a re-measure. */
  remeasureKey: string;
}

export interface AdaptiveTabFit {
  itemWidths: number[];
  moreWidth: number;
  containerWidth: number;
  /** Computed column-gap of the live row in px (32 when unparseable). */
  gapWidth: number;
  /** False until every tab + More has a non-zero measured width. */
  measured: boolean;
}

export function useAdaptiveTabFit({
  containerRef,
  rowRef,
  itemRefs,
  moreRef,
  itemCount,
  remeasureKey,
}: UseAdaptiveTabFitArgs): AdaptiveTabFit {
  // A single snapshot keeps the measurements consistent and lets the
  // re-measure path bail out when nothing changed (so the layout effect cannot
  // loop — the setState only fires on a genuine geometry change).
  const [snapshot, setSnapshot] = useState<{
    itemWidths: number[];
    moreWidth: number;
    containerWidth: number;
    gapWidth: number;
  }>({
    itemWidths: [],
    moreWidth: 0,
    containerWidth: 0,
    gapWidth: FALLBACK_GAP_PX,
  });

  // Measure-and-store, defined once and shared by the layout effect and the
  // ResizeObserver. The functional setState compares against the previous
  // snapshot and returns it unchanged when the geometry is identical, so a
  // re-measure with no real change is a no-op (no re-render, no loop).
  useLayoutEffect(() => {
    const remeasure = () => {
      const els = itemRefs.current ?? [];
      const itemWidths = Array.from({ length: itemCount }, (_, i) =>
        els[i] ? els[i]!.getBoundingClientRect().width : 0,
      );
      const moreWidth = moreRef.current?.getBoundingClientRect().width ?? 0;
      const containerWidth =
        containerRef.current?.getBoundingClientRect().width ?? 0;
      // The live row's CSS gap is the single source of truth for inter-tab
      // spacing; reading it here keeps the fit math in lockstep with a
      // styling change to the row.
      const rowEl = rowRef.current;
      const parsedGap = rowEl
        ? Number.parseFloat(getComputedStyle(rowEl).columnGap)
        : Number.NaN;
      const gapWidth = Number.isNaN(parsedGap) ? FALLBACK_GAP_PX : parsedGap;
      setSnapshot((prev) =>
        prev.moreWidth === moreWidth &&
        prev.containerWidth === containerWidth &&
        prev.gapWidth === gapWidth &&
        prev.itemWidths.length === itemWidths.length &&
        prev.itemWidths.every((w, i) => w === itemWidths[i])
          ? prev
          : { itemWidths, moreWidth, containerWidth, gapWidth },
      );
    };

    remeasure();

    // Re-measure on container resize (viewport or sidebar change). Guard for
    // environments without ResizeObserver support (jsdom installs a no-op stub).
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(remeasure);
    ro.observe(el);
    return () => ro.disconnect();
    // remeasureKey is a serialised badge-count string: a change widens a tab,
    // so the geometry must be re-read.
  }, [containerRef, rowRef, itemRefs, moreRef, itemCount, remeasureKey]);

  const { itemWidths, moreWidth, containerWidth, gapWidth } = snapshot;
  const measured =
    itemWidths.length === itemCount &&
    itemWidths.every((w) => w > 0) &&
    moreWidth > 0;

  return { itemWidths, moreWidth, containerWidth, gapWidth, measured };
}
